import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, X, Shield, Award, Zap, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const AMCServices = () => {
  const amcPlans = [
    {
      name: 'Basic AMC',
      price: 'Starting ₹15,000/year',
      recommended: false,
      features: [
        { text: '2 Preventive Maintenance Visits', included: true },
        { text: 'Breakdown Support (Chargeable)', included: true },
        { text: 'Spare Parts (Extra Cost)', included: false },
        { text: 'Consumables (Not Included)', included: false },
        { text: 'Phone & WhatsApp Support', included: true },
        { text: 'Priority Service', included: false },
        { text: 'Remote Diagnostics', included: true },
        { text: 'Annual Health Report', included: true }
      ],
      ideal: 'Small plants with basic maintenance needs'
    },
    {
      name: 'Standard AMC',
      price: 'Starting ₹35,000/year',
      recommended: true,
      features: [
        { text: '4 Preventive Maintenance Visits', included: true },
        { text: 'Breakdown Support (Free)', included: true },
        { text: 'Spare Parts (10% Discount)', included: true },
        { text: 'Basic Consumables (Included)', included: true },
        { text: 'Phone, WhatsApp & Email Support', included: true },
        { text: 'Priority Service', included: true },
        { text: 'Remote Diagnostics', included: true },
        { text: 'Quarterly Performance Reports', included: true }
      ],
      ideal: 'Medium-scale plants with regular production'
    },
    {
      name: 'Comprehensive AMC',
      price: 'Starting ₹65,000/year',
      recommended: false,
      features: [
        { text: '6 Preventive Maintenance Visits', included: true },
        { text: 'Unlimited Breakdown Support', included: true },
        { text: 'Spare Parts (20% Discount)', included: true },
        { text: 'All Consumables (Included)', included: true },
        { text: '24/7 Dedicated Support', included: true },
        { text: 'Highest Priority Service', included: true },
        { text: 'On-Site Engineer (on-demand)', included: true },
        { text: 'Monthly Performance Reports', included: true }
      ],
      ideal: 'Large industrial plants requiring maximum uptime'
    }
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="Annual Maintenance Contracts (AMC) for Water Plants | Alden Technologies"
        description="Protect your investment with Alden Technologies AMC plans — scheduled maintenance, priority breakdown support and genuine spares for water bottling machinery."
        path="/amc-services"
      />
      <Breadcrumbs items={[{ label: 'AMC Services' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green-dark via-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-3 rounded-full text-sm font-semibold mb-6">
            🔧 Annual Maintenance Contracts
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            AMC <span className="text-yellow-300">Service Plans</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
            Choose the perfect Annual Maintenance Contract plan for your plant. Ensure maximum uptime, minimize breakdowns, and extend equipment life.
          </p>
        </div>
      </section>

      {/* AMC Plans Comparison */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your <span className="text-alden-green">AMC Plan</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Flexible annual maintenance contracts tailored to your plant capacity and production requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {amcPlans.map((plan) => (
              <Card key={`amc-${plan.name.toLowerCase().replace(/\s+/g, '-')}`} className={`relative overflow-hidden $${
                plan.recommended 
                  ? 'border-4 border-alden-green shadow-2xl scale-105' 
                  : 'border-2 border-alden-green/20 hover:border-alden-green'
              } transition-all`}>
                {plan.recommended && (
                  <div className="absolute top-0 right-0 bg-yellow-400 text-alden-navy px-6 py-2 font-bold text-sm">
                    ⭐ RECOMMENDED
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-3xl font-bold text-alden-green mb-4">{plan.price}</div>
                    <p className="text-sm text-gray-600 italic">{plan.ideal}</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature) => (
                      <li key={`${plan.name}-${feature.text.slice(0, 20)}`} className="flex items-start gap-3">
                        {feature.included ? (
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`text-sm ${
                          feature.included ? 'text-gray-700' : 'text-gray-400 line-through'
                        }`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button className={`w-full py-6 text-lg font-semibold ${
                      plan.recommended
                        ? 'bg-alden-green hover:bg-alden-green-light text-white'
                        : 'bg-gray-800 hover:bg-gray-900 text-white'
                    }`}>
                      Select {plan.name}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              * Pricing varies based on plant capacity and machine configuration. Contact us for customized quotation.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose AMC */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-alden-green">AMC?</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Benefits of Annual Maintenance Contract for your mineral water plant.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { id: 'amc-benefit-uptime', icon: Shield, title: 'Maximize Uptime', desc: 'Regular maintenance prevents unexpected breakdowns and production loss.' },
              { id: 'amc-benefit-life', icon: Award, title: 'Extend Equipment Life', desc: 'Proper maintenance significantly extends machinery lifespan and ROI.' },
              { id: 'amc-benefit-priority', icon: Zap, title: 'Priority Support', desc: 'Get priority response and faster resolution during breakdowns.' },
              { id: 'amc-benefit-savings', icon: CheckCircle, title: 'Cost Savings', desc: 'Preventive maintenance costs less than emergency repairs and downtime.' },
              { id: 'amc-benefit-reports', icon: Shield, title: 'Performance Reports', desc: 'Regular health reports help track equipment performance trends.' },
              { id: 'amc-benefit-parts', icon: Award, title: 'Genuine Spare Parts', desc: 'Access to genuine spare parts at discounted rates with warranty.' }
            ].map((benefit) => (
              <Card key={benefit.id} className="border-2 border-alden-green/20 hover:border-alden-green transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-10 h-10 text-alden-green" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get Customized AMC Quote
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Contact our service team for a customized AMC plan based on your plant capacity and requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-10 text-lg font-semibold">
                <Mail className="mr-2" />
                Request AMC Quote
              </Button>
            </Link>
            <a href="tel:+919351292512">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-10 text-lg font-semibold">
                <Phone className="mr-2" />
                Call: +91 9351292512
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AMCServices;